import { zodResolver } from '@hookform/resolvers/zod';
import { AccountProductEnum } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useUpdateNickname from 'features/accounts/ManageAccount/ManageAccountNickname/hooks/useUpdateNickname';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetAccountR2 from 'services/graphql/queries/AccountR2';
import Button from 'shared/components/vxl/Button/Button';
import { accountNicknameSchema } from 'shared/components/vxl/input/schemas/accountNickname.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';
import { z } from 'zod';

const nicknameSchema = z.object({
  nickname: accountNicknameSchema,
});

type NicknameFormValues = z.infer<typeof nicknameSchema>;

const EditAccountNameScreen = () => {
  const router = useRouter();
  const { accountId, accountType } = useLocalSearchParams<{
    accountId: string;
    accountType: AccountProductEnum;
  }>();

  const { t: translatedText } = useTranslation('accounts');
  const insets = useSafeAreaInsets();

  const [{ data: accountData }] = useQuery({
    query: GetAccountR2,
    variables: { id: accountId ?? '' },
    requestPolicy: 'cache-first',
    pause: !accountId,
  });

  const currentNickname = accountData?.accountR2?.nickname ?? '';

  const { loadingNickName, handleSavePress } = useUpdateNickname(
    accountId ?? '',
    accountType,
  );

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<NicknameFormValues>({
    resolver: zodResolver(nicknameSchema),
    defaultValues: { nickname: currentNickname },
    mode: 'onChange',
  });

  useEffect(() => {
    if (currentNickname) {
      reset({ nickname: currentNickname });
    }
  }, [currentNickname, reset]);

  const nicknameValue = watch('nickname');

  const onSave = handleSubmit(async ({ nickname: newNickname }) => {
    try {
      const result = await handleSavePress(newNickname);

      if (result?.error) {
        throw result.error;
      }

      router.back();
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }

      setError('nickname', {
        message: 'manageAccount.editAccountName.saveError',
      });
    }
  });

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loadingNickName}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />
      <View style={styles.content}>
        <TitleLgText numberOfLines={0}>
          {translatedText('manageAccount.editAccountName.screenTitle')}
        </TitleLgText>
        <Controller
          control={control}
          name="nickname"
          render={({ field: { onChange, value } }) => (
            <VXLTextInput
              labelText={translatedText('manageAccount.accountName')}
              value={value}
              onChangeText={onChange}
              errorText={
                errors.nickname?.message
                  ? translatedText(errors.nickname.message)
                  : undefined
              }
              maxLength={25}
              returnKeyType="done"
              onSubmitEditing={onSave}
              testID="input-edit-account-name"
              autoFocus
            />
          )}
        />
      </View>
      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('manageAccount.editAccountName.saveButton')}
          onPress={() => onSave()}
          type="PRIMARY"
          width="FULL"
          isDisabled={!nicknameValue || loadingNickName}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    paddingVertical: theme.spacing.m,
    gap: theme.spacing.m,
    alignSelf: 'stretch',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  content: {
    gap: theme.spacing.m,
  },
});

export default EditAccountNameScreen;
